import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FIELD_LIMITS } from '@aws-demo-platform/shared';

export interface SearchBarProps {
  /** Called with the search term after debounce (300ms) */
  onSearch: (term: string) => void;
  /** Placeholder text for the input */
  placeholder?: string;
}

/**
 * SearchBar component with debounced input (300ms).
 * Validates search term length (1-100 characters).
 * Shows character count when approaching the 100-char limit.
 * Provides a clear button to reset the search.
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder = 'Search demos by title, description, or project...',
}) => {
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const maxLength = FIELD_LIMITS.SEARCH_TERM_MAX;
  const charWarningThreshold = 80;

  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onSearch(term);
      }, 300);
    },
    [onSearch]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Prevent input beyond 100 characters
    if (value.length > maxLength) {
      return;
    }

    setInputValue(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    setInputValue('');
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onSearch('');
  };

  const showCharCount = inputValue.length >= charWarningThreshold;
  const charCountClass = inputValue.length >= maxLength
    ? 'search-charcount search-charcount--limit'
    : inputValue.length >= charWarningThreshold
    ? 'search-charcount search-charcount--warning'
    : 'search-charcount';

  return (
    <div className="search-container">
      <div className="search-input-wrapper">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-label="Search demos"
          className="search-input"
        />
        {inputValue.length > 0 && (
          <button
            onClick={handleClear}
            aria-label="Clear search"
            className="search-clear"
          >
            ✕
          </button>
        )}
      </div>
      {showCharCount && (
        <div className={charCountClass} aria-live="polite">
          {inputValue.length}/{maxLength}
        </div>
      )}
    </div>
  );
};
